import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import {
  DropdownItem,
  Order,
  OrderComponent,
} from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "multi-option",
  templateUrl: "multi-option.html",
})
export class MultiOptionComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    this._orderFormState = orderFormState;
    if (this.orderCreateRequest.orderType === "LEVEL_OF_CARE") {
      if (this.orderComponent.componentLabel === "Symptoms") {
        this.isLevelOfCare =
          this._orderFormState.levelOfCare === "continuousCare";
      }

      if (this._orderFormState.levelOfCare !== "continuousCare") {
        this.selectedOptions = [];
        if (
          this.orderCreateRequest.levelOfCareSymptoms !== undefined ||
          this.orderCreateRequest.levelOfCareSymptomsOther !== undefined
        ) {
          const updateObject: OrderCreateRequestUpdate = {};
          updateObject.deletes = [
            "levelOfCareSymptoms" as keyof Order,
            "levelOfCareSymptomsOther" as keyof Order,
          ];
          this.orderCreateRequestEvent.emit(updateObject);
        }
      }
    }
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  optionsDropdownItems: DropdownItem[] = [];
  selectedOptions: string[] = [];
  optionOtherDescription: string;
  isLevelOfCare = true;
  _orderFormState: OrderFormState;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.setOptionsDropdownItems(this.orderComponent);
    this._orderFormState.formType === "amendment"
      ? this.setAmendedValue()
      : null;
    this.checkIsValid();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.orderComponent.currentValue) {
      this.setOptionsDropdownItems(changes.orderComponent.currentValue);
    }
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get isOtherSelected() {
    return this.selectedOptions.includes("other");
  }

  get isDisabled() {
    return (
      !this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    );
  }

  getLabel(dropdownItem: DropdownItem): string {
    if (dropdownItem.value == "other" && this.optionOtherDescription) {
      return `Other (${this.optionOtherDescription})`;
    }
    return dropdownItem.label;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setOptionsDropdownItems(orderComponent: OrderComponent): void {
    if (orderComponent.properties[0]) {
      this.optionsDropdownItems = orderComponent.properties[0].dropdownItems;
    }
  }

  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    // no value to set, return
    if (!this.orderCreateRequest[componentField]) {
      return;
    }
    this.selectedOptions = this.orderCreateRequest[componentField];
    if (this.orderCreateRequest.categoryOtherDescription) {
      this.optionOtherDescription =
        this.orderCreateRequest.categoryOtherDescription;
    }
  }

  /****************************************************************************
   * Change Events                                                            *
   ****************************************************************************/
  onOptionsChange(event: string[]) {
    if (event) {
      const componentField = this.orderComponent.properties[0].name;
      if (
        this.orderComponent.properties[0].amendable &&
        this._orderFormState.formType === "amendment"
      ) {
        const prevValueOrEmptyArray = this.orderCreateRequest[componentField]
          ? this.orderCreateRequest[componentField]
          : [];
        event = event.filter(function (item) {
          return item !== "other";
        });
        if (JSON.stringify(prevValueOrEmptyArray) !== JSON.stringify(event)) {
          const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
          const filteredLabels = this.optionsDropdownItems
            .filter((item) => prevValueOrEmptyArray.includes(item.value))
            .map((item) => item.label);

          const resultCategories = filteredLabels.join(", ");

          amendedObject.updates = {
            propertyName: componentField,
            previousValueDisplay: resultCategories,
            previousValue: this.orderCreateRequest[componentField],
            newValue: Array.from(new Set(event)),
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        } else {
          const amendedObject: OrderComponentPropertyUpdate = {
            updates: {},
            deletes: [componentField as keyof Order],
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        }
      } else {
        switch (componentField) {
          case "categories":
            this.orderCreateRequestEvent.emit({
              updates: {
                categories: new Set(event),
                categoryOtherDescription: this.optionOtherDescription,
              },
            });
            break;
          case "levelOfCareSymptoms":
            this.orderCreateRequestEvent.emit({
              updates: {
                levelOfCareSymptoms: new Set(event),
                levelOfCareSymptomsOther: this.optionOtherDescription,
              },
            });
            break;
          default:
        }
      }
    }
    this.checkIsValid();
  }

  onOptionOtherDescriptionChange(event: string) {
    const componentField = this.orderComponent.properties[0].name;
    const amendable = this.orderComponent.properties[0].amendable;
    const isAmendment = this._orderFormState.formType === "amendment";
    const prevValue = this.orderCreateRequest["categoryOtherDescription"];

    if (event) {
      if (amendable && isAmendment) {
        const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
        if (prevValue !== event) {
          amendedObject.updates = {
            propertyName: "categoryOtherDescription",
            previousValueDisplay: prevValue ? prevValue : "",
            previousValue: prevValue ? prevValue : "",
            newValue: event,
          };
        } else {
          amendedObject.deletes = ["categoryOtherDescription"];
        }

        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        switch (componentField) {
          case "categories":
            this.orderCreateRequestEvent.emit({
              updates: {
                categoryOtherDescription: event,
              },
            });
            break;
          case "levelOfCareSymptoms":
            this.orderCreateRequestEvent.emit({
              updates: {
                levelOfCareSymptoms: new Set(this.selectedOptions),
                levelOfCareSymptomsOther: event,
              },
            });
            break;
          default:
        }
      }
    } else {
      if (amendable && isAmendment) {
        const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
        if (prevValue ? prevValue : "" !== event) {
          amendedObject.updates = {
            propertyName: "categoryOtherDescription",
            previousValueDisplay: prevValue ? prevValue : "",
            previousValue: prevValue ? prevValue : "",
            newValue: event,
          };
        } else {
          amendedObject.deletes = ["categoryOtherDescription"];
        }

        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        switch (componentField) {
          case "categories":
            this.orderCreateRequestEvent.emit({
              updates: {
                categoryOtherDescription: event,
              },
            });
            break;
          case "levelOfCareSymptoms":
            this.orderCreateRequestEvent.emit({
              updates: {
                levelOfCareSymptoms: new Set(this.selectedOptions),
                levelOfCareSymptomsOther: event,
              },
            });
            break;
          default:
        }
      }
    }
  }

  optionSelected(dropdownItem: DropdownItem): boolean {
    return this.selectedOptions.includes(dropdownItem.value);
  }

  checkIsValid() {
    if (
      this.orderComponent.properties[0].required &&
      this.selectedOptions.length === 0
    ) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/multi-option/multi-option.ts